import React, { useEffect, useState } from "react";

import HttpManager from "../../services/HttpManager";
import { diaEtiqueta, hora } from "../../navigation/v2/sales/salesHelpers";
import { Aprobacion, AprobacionLinea, MIN_MOTIVO } from "./tipos";

/*
	La tarjeta de una aprobación (CONTRATO_PROMO_EN_CAMINO §2, ESPEC §6.2). Se dibuja igual en la burbuja de WappTeam y en la ventana
	«Cambiar Promoción» (ahí con «Retirar»). Es genérica: título = kindName, y los renglones salen de `lines` agrupados por persona,
	así que eximir meses, recargos, etc. (fase 2) no necesitan tocarla. La primera que decide gana: al decidir se vuelve a pedir
	GET aprobaciones/{id} para que la tarjeta enseñe quién decidió.
*/

export interface TarjetaAprobacionProps {
	a: Aprobacion;
	/** Cambió el estado (decidida o retirada): el padre puede recargar lo suyo. */
	onCambio?: (a: Aprobacion) => void;
	/** «Ver en la Ficha». Sin esta función el botón no sale. */
	onVerFicha?: (a: Aprobacion) => void;
}

const http = HttpManager.getInstance();

/** «3:15 PM» si fue hoy; «ayer 3:15 PM», «12 oct 3:15 PM» si no. */
const cuandoTexto = (v: string | null): string => {
	if (!v) return "";
	const h = hora(v).toUpperCase();
	const d = diaEtiqueta(v);
	return d === "hoy" ? h : d + " " + h;
};

const errorDe = (e: any): string => e?.response?.data?.message || e?.response?.data?.error || e?.message || "No se pudo completar. Intenta otra vez.";

/** Renglones agrupados por persona, en el orden en que llegan. */
const porPersona = (lines: AprobacionLinea[]): { person: string | null; lines: AprobacionLinea[] }[] => {
	const out: { person: string | null; lines: AprobacionLinea[] }[] = [];
	lines.forEach((l) => {
		const p = l.person || null;
		const g = out.find((x) => x.person === p);
		if (g) g.lines.push(l); else out.push({ person: p, lines: [l] });
	});
	return out;
};

/** Los renglones agrupados por persona; los que le suben algo a alguien van en rojo. Los usa la tarjeta y la ventana «Enviar a Aprobación». */
export const LineasAprobacion: React.FC<{ lines: AprobacionLinea[] }> = ({ lines }) => (
	<>
		{porPersona(lines).map((g, i) => (
			<div key={"g" + i} style={{ marginTop: 8 }}>
				{g.person ? <div style={{ fontWeight: 700 }}>{g.person}</div> : null}
				{g.lines.map((l, j) => (
					<div key={"l" + j} style={{ color: l.up ? "var(--wa-rojo)" : "var(--wa-tinta)", fontWeight: l.up ? 600 : 400, paddingLeft: g.person ? 8 : 0 }}>{l.text}</div>
				))}
			</div>
		))}
	</>
);

const TarjetaAprobacion: React.FC<TarjetaAprobacionProps> = ({ a: inicial, onCambio, onVerFicha }) => {
	const [a, setA] = useState<Aprobacion>(inicial);
	const [rechazando, setRechazando] = useState<boolean>(false);
	const [motivo, setMotivo] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [recordado, setRecordado] = useState<boolean>(false);
	useEffect(() => setA(inicial), [inicial]);
	useEffect(() => {
		if (!recordado) return;
		const t = window.setTimeout(() => setRecordado(false), 3000);
		return () => window.clearTimeout(t);
	}, [recordado]);

	/** Vuelve a pedir la aprobación: lo que decidió otra persona o el servidor se ve igual en todas las burbujas. */
	const refrescar = async (respaldo?: Aprobacion) => {
		try {
			const r = await http.aprobacionesService.get(a.campusId, a.id);
			setA(r.data);
			onCambio?.(r.data);
		} catch {
			if (respaldo) { setA(respaldo); onCambio?.(respaldo); }
		}
	};
	const correr = async (fn: () => Promise<{ data: Aprobacion }>) => {
		try {
			setBusy(true);
			setError(null);
			const r = await fn();
			setRechazando(false);
			setMotivo("");
			await refrescar(r.data);
		} catch (e: any) {
			setError(errorDe(e));
			// 409: ya la decidió otra persona o la cuenta cambió; se enseña como quedó.
			if (e?.response?.status === 409) await refrescar();
		} finally {
			setBusy(false);
		}
	};
	const aprobar = () => correr(() => http.aprobacionesService.decidir(a.campusId, a.id, "aprobar"));
	const rechazar = () => correr(() => http.aprobacionesService.decidir(a.campusId, a.id, "rechazar", motivo.trim()));
	const retirar = () => correr(() => http.aprobacionesService.retirar(a.campusId, a.id));
	/** Otro DM a cada aprobadora, tantas veces como quiera quien la pidió (el servidor pide un minuto entre uno y otro). */
	const recordar = async () => {
		try {
			setBusy(true);
			setError(null);
			setRecordado(false);
			setA((await http.aprobacionesService.recordar(a.campusId, a.id)).data);
			setRecordado(true);
		} catch (e: any) {
			setError(errorDe(e));
			if (e?.response?.status === 409) await refrescar();
		} finally {
			setBusy(false);
		}
	};
	const nRec = a.reminders || 0;

	const pendiente = a.status === "pendiente";
	const por = a.decidedBy?.name || "";
	const decision = a.status === "aprobada" ? `Aprobado por ${por}, ${cuandoTexto(a.decidedAt)}`
		: a.status === "rechazada" ? `Rechazado por ${por}${a.decisionReason ? ": " + a.decisionReason : ""}`
			: a.status === "retirada" ? `Retirada${por ? " por " + por : ""}` : a.statusName;
	const grupos = porPersona(a.lines || []);

	return (
		<div role="group" aria-label={a.kindName} onClick={(e) => e.stopPropagation()}
			style={{ width: "min(380px, 100%)", minWidth: "min(300px, 72vw)", boxSizing: "border-box", textAlign: "left", fontSize: 14, color: "var(--wa-tinta)", whiteSpace: "normal",
				background: "var(--wa-superficie)", border: "1px solid var(--wa-filo)", borderLeft: "4px solid " + (pendiente ? "var(--wa-oro)" : "var(--wa-acento)"), borderRadius: 8, padding: "10px 12px" }}>
			<div style={{ fontWeight: 700 }}>{a.kindName}</div>
			{a.studentName ? <div style={{ fontWeight: 600 }}>{a.studentName}</div> : null}
			{a.summary && !grupos.length ? <div>{a.summary}</div> : null}
			<LineasAprobacion lines={a.lines || []} />
			{a.reason ? <div style={{ marginTop: 8 }}><span style={{ color: "var(--wa-apagado)" }}>Motivo:</span> {a.reason}</div> : null}
			{a.requestedBy ? <div style={{ marginTop: 4, fontSize: 12, color: "var(--wa-apagado)" }}>Pidió {a.requestedBy.name}{a.requestedAt ? ", " + cuandoTexto(a.requestedAt) : ""}{nRec > 0 ? " · " + nRec + (nRec === 1 ? " recordatorio" : " recordatorios") : ""}</div> : null}

			{!pendiente ? (
				<div style={{ marginTop: 8, fontWeight: 700, color: a.status === "rechazada" || a.status === "sinAprobar" || a.status === "caducada" ? "var(--wa-oro)" : "var(--wa-acento-texto)" }}>{decision}</div>
			) : rechazando ? (
				<div style={{ marginTop: 8 }}>
					<textarea className="form-control form-control-sm" rows={2} autoFocus style={{ fontSize: 16 }} placeholder="Motivo del Rechazo" value={motivo} disabled={busy} onChange={(e) => setMotivo(e.target.value)} />
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
						<span style={{ fontSize: 12, color: motivo.trim().length >= MIN_MOTIVO ? "var(--wa-apagado)" : "var(--wa-oro)" }}>{motivo.trim().length}/{MIN_MOTIVO}</span>
						<span style={{ display: "flex", gap: 6 }}>
							<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => { setRechazando(false); setMotivo(""); setError(null); }}>Atrás</button>
							<button type="button" className="btn btn-danger btn-sm" disabled={busy || motivo.trim().length < MIN_MOTIVO} onClick={rechazar}>Rechazar</button>
						</span>
					</div>
				</div>
			) : (
				<div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
					{a.canDecide ? <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={aprobar}>Aprobar</button> : null}
					{a.canDecide ? <button type="button" className="btn btn-outline-danger btn-sm" disabled={busy} onClick={() => { setRechazando(true); setError(null); }}>Rechazar</button> : null}
					{a.canWithdraw ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={retirar}>Retirar</button> : null}
					{a.canRemind ? <button type="button" className="btn btn-outline-primary btn-sm" disabled={busy} onClick={recordar}>Enviar Otro Recordatorio</button> : null}
					{onVerFicha && a.link ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => onVerFicha(a)}>Ver en la Ficha</button> : null}
				</div>
			)}
			{!pendiente && onVerFicha && a.link ? (
				<div style={{ marginTop: 6 }}><button type="button" className="btn btn-link btn-sm" style={{ padding: 0 }} onClick={() => onVerFicha(a)}>Ver en la Ficha</button></div>
			) : null}
			{recordado && pendiente ? <div role="status" style={{ marginTop: 8, color: "var(--wa-acento-texto)", fontWeight: 600, fontSize: 13 }}>Recordatorio enviado</div> : null}
			{error ? <div role="alert" style={{ marginTop: 8, color: "var(--wa-rojo-error)", fontWeight: 600, fontSize: 13 }}>{error}</div> : null}
		</div>
	);
};

export default TarjetaAprobacion;
